"use client";

import React, { useEffect, useRef, useState } from "react";
import { CheckCircle2, Info, Wrench, ShieldCheck, Settings2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "50px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const commonProblems = [{
  title: "Encrypted Connectivity",
  description: "Our secure networks ensure your professional work stays private while you enjoy our communal workspace environment.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603628/img/coworking_privacy_1.png"
}, {
  title: "Member Confidentiality",
  description: "We protect your personal data with the same integrity we apply to our shared community values and workspace rules.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603628/img/coworking_privacy_2.png"
}];
const repairSteps = [{
  icon: Settings2,
  title: "Purposeful Collection",
  description: "We only gather the essentials needed to provide your membership services and improve your daily workspace experience."
}, {
  icon: Wrench,
  title: "Secure Infrastructure",
  description: "Your data is stored in encrypted systems that meet rigorous security standards to prevent unauthorized access or leaks."
}, {
  icon: ShieldCheck,
  title: "Privacy Transparency",
  description: "We believe in being clear about how we use your information, ensuring you always feel in control of your data."
}];
const inspectedParts = ["Membership Profile Details", "Secure Payment Information", "Meeting Room Bookings", "Network Access Credentials", "Facility Entry Logs", "Communication Preferences"];
export default function RepairDetails() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: problemsRef,
    isVisible: areProblemsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: processRef,
    isVisible: isProcessVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: partsRef,
    isVisible: arePartsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="152566" id="privacy-overview" className="bg-background py-24 overflow-hidden pt-[120px]">
      <div className="container mx-auto px-4 max-w-5xl">
        {}
        <div ref={headerRef} className={`transition-all duration-1000 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <Alert className="bg-transparent border-none shadow-none mb-20 py-0 px-0 rounded-none backdrop-blur-none max-w-[800px] mx-auto text-center">
            <Info className="hidden" />
            <AlertTitle className="text-foreground font-bold text-[3.5rem] mb-6 font-default tracking-normal">
              Privacy Policy
            </AlertTitle>
            <AlertDescription className="text-foreground/80 font-sans text-xl leading-relaxed">
              At BLUVIT, we handle your data like we handle our shared space: with care, respect, and a commitment to security. Your trust is our foundation.
            </AlertDescription>
          </Alert>
        </div>

        {}
        <div ref={problemsRef} className={`mb-24 transition-all duration-1000 delay-100 transform ${areProblemsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-default tracking-tight">
              Our Privacy Principles
            </h2>
            <div className="h-1 w-24 bg-primary rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {commonProblems.map((problem, i) => <Card key={i} data-index={i} className="border-border shadow-sm overflow-hidden group bg-card backdrop-blur-sm transition-all duration-300 hover:shadow-md rounded-[24px]">
                <div className="overflow-hidden h-64 relative">
                  <div className="absolute inset-0 bg-primary/5 mix-blend-overlay z-10 transition-opacity duration-500 group-hover:opacity-0"></div>
                  <img src={problem.image} alt={problem.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-default text-foreground">
                    {problem.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-sans text-lg text-muted-foreground leading-relaxed">
                    {problem.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        {}
        <div ref={processRef} className={`mb-24 transition-all duration-1000 delay-200 transform ${isProcessVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-default tracking-tight">
              Data Stewardship Process
            </h2>
            <div className="h-1 w-24 bg-primary rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {repairSteps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative">
                  {i !== repairSteps.length - 1 && <div className="hidden md:block absolute top-8 left-[60%] w-full h-[2px] bg-border -z-10"></div>}
                  <div className="bg-card border border-border shadow-sm rounded-[24px] p-8 h-full flex flex-col items-start relative z-10">
                    <div className="bg-primary/10 p-4 rounded-xl mb-6 text-primary">
                      <Icon className="size-8" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-4 font-default">
                      {step.title}
                    </h3>
                    <p className="font-sans text-lg text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={partsRef} className={`transition-all duration-1000 delay-300 transform ${arePartsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 font-default tracking-tight">
              Data Categories We Protect
            </h2>
            <div className="h-1 w-24 bg-primary rounded-full"></div>
          </div>

          <Card className="border-border shadow-sm bg-card/80 backdrop-blur-md rounded-[24px]">
            <CardContent className="p-8 md:p-12">
              <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-8 max-w-3xl">
                Ensuring your privacy requires a comprehensive approach. We proactively monitor and protect these key data points to ensure your coworking experience remains secure and professional.
              </p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {inspectedParts.map((part, i) => <div key={i} data-index={i} className="flex items-center gap-4 p-3 rounded-lg hover:bg-accent transition-colors">
                    <CheckCircle2 className="size-6 text-primary shrink-0" />
                    <span className="font-sans text-lg text-foreground font-medium">
                      {part}
                    </span>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}